import {Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, Validators } from '@angular/forms';
import {NGXLogger} from 'ngx-logger';

@Component({
  selector: 'app-checkbox',
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CheckboxComponent),
      multi: true
    }
  ]
})
export class CheckboxComponent implements OnInit, OnChanges, ControlValueAccessor, Validator {

  // Functions needed to manage the ngModel of this component
  private onChange: (_: any) => void;
  private onTouched: any;

  public inputFormControl = new UntypedFormControl(false);

  @Input() checked = false;
  /**
   * Set to true to disable the checkbox.
   *
   * Note: Will be an empty string if attribute is set without a value like this: `<my-element disabled></my-element>`
   */
  @Input() disabled: boolean|'' = false;
  /**
   * Set to true to require the checkbox to be checked.
   *
   * Note: Will be an empty string if attribute is set without a value like this: `<my-element required></my-element>`
   */
  @Input() required: boolean|'' = false;
  @Input() caption = '';
  @Input() popup = '';
  @Input() isReport = false;
  @Input() isWarningPopup = false;

  @Output() checkedChange = new EventEmitter<boolean>();


  public uniqueId = this.caption;

  constructor(public log: NGXLogger) { }

  ngOnInit(): void {
    const rnd = Math.floor(Math.random() * (999999 - 100001)) + 100001;
    this.uniqueId = this.uniqueId + '_' + rnd.toString();

    this.inputFormControl.valueChanges.subscribe(value => {
      this.setValue(value);
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.checked) {
      this.setValue(this.checked, true);
    }

    if (changes.disabled) {
      if (this.disabled === true || this.disabled === '') {
        this.inputFormControl.disable({ emitEvent: false });
      } else {
        this.inputFormControl.enable({ emitEvent: false });
      }
    }

    if (changes.required) {
      // We have to add/remove validators here instead of using the html template because if `required` is initially set,
      // this would not be registered by FormControl. So we set the validator manually here.
      if (this.required === true || this.required === '') {
        this.inputFormControl.addValidators(Validators.requiredTrue);
      } else {
        this.inputFormControl.removeValidators(Validators.requiredTrue);
      }
      this.inputFormControl.updateValueAndValidity();
    }
  }

  public writeValue(value: boolean) {
    if (value == null) {
      return;
    }

    this.setValue(value, true);
  }

  public registerOnChange(fn: (_: any) => void) {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  public validate({value}: UntypedFormControl): ValidationErrors | null {
    return this.inputFormControl.errors;
  }

  public onInputBlur() {
    if (this.onTouched) {
      this.onTouched();
    }
  }

  /**
   * @param externalChange Set to true if the value change was not caused by a change inside this component.
   *                       Then, ControlValueAccessor won't be notified about changes because this would result in a loop.
   */
   private setValue(value: boolean, externalChange = false) {
    // Set emitEvent to false to prevent a loop
    this.inputFormControl.setValue(value, { emitEvent: false });

    if (!externalChange) {
      this.checked = value;
      this.checkedChange.emit(value);

      if (this.onChange) {
        this.onChange(value);
      }
    }
  }
}
